/**
 * @description Hook to get the expenses for a specific voucher employee
 * @param {string} voucherEmployeeId - The id of the voucher employee to get the expenses for
 * @param {number} staleTime - The time in milliseconds after which the data is considered stale
 * @param {number} gcTime - The time in milliseconds after which the data is garbage collected
 * @param {boolean} refetchOnWindowFocus - Whether to refetch the data when the window is focused
 * @param {boolean} refetchOnMount - Whether to refetch the data when the component is mounted
 * @returns {UseExpensesReturn} - The expenses for the specific voucher employee
 */

import { useQuery } from "@tanstack/react-query";
import { getExpenses } from "@/features/vouchers/services/expense.service";
import type { ExpensesResponse } from "@/features/vouchers/types/expense.type";

export const useExpenses = (voucherEmployeeId?: string) => {
  return useQuery<ExpensesResponse>({
    queryKey: ["expenses", voucherEmployeeId],
    queryFn: () => getExpenses(voucherEmployeeId),
    enabled: !!voucherEmployeeId,
    staleTime: 2 * 60 * 1000,
    gcTime: 5 * 60 * 1000,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
  });
};
